import React from "react";
import { Badge } from "@mantine/core";
import type { DataTableInfiniteScroll } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { MonitoringEvent } from "@models/adminMonitoring";
import { AdminTable, col } from "@components/AdminTable";
import { formatCount, moduleLabel, shortRef } from "@utils/monitoringFormat";

const units = (event: MonitoringEvent): string => {
  const parts: string[] = [];
  if (event.input_tokens != null || event.output_tokens != null) {
    parts.push(`${formatCount(event.input_tokens ?? 0)} / ${formatCount(event.output_tokens ?? 0)} tok`);
  }
  if (event.pages_processed) parts.push(`${formatCount(event.pages_processed)} pg`);
  if (event.images) parts.push(`${formatCount(event.images)} img`);
  return parts.join(" · ") || "-";
};

interface EventsTableProps {
  items: MonitoringEvent[];
  loading: boolean;
  showOrganisation: boolean;
  infiniteScroll?: DataTableInfiniteScroll;
  maxHeight?: number | string;
}

/** Raw metering events, newest first. */
const EventsTable: React.FC<EventsTableProps> = ({ items, loading, showOrganisation, infiniteScroll, maxHeight }) => {
  const { t } = useTranslation();
  const columns = [
    col.date<MonitoringEvent>("time", t("admin.insights.columns.time"), (e) => e.occurred_at),
    col.twoLine<MonitoringEvent>("service", t("admin.insights.columns.moduleService"), (e) => ({
      primary: moduleLabel(e.module_name), secondary: e.service_name,
    }), { width: "15%" }),
    col.twoLine<MonitoringEvent>("model", t("admin.insights.columns.providerModel"), (e) => ({
      primary: e.provider ?? "-", secondary: e.model_key,
    }), { width: "16%" }),
    col.badge<MonitoringEvent>("capability", t("admin.insights.columns.capability"),
      (e) => <Badge size="sm" variant="light">{e.capability || "-"}</Badge>),
    col.text<MonitoringEvent>("units", t("admin.insights.columns.units"), units, { width: "14%" }),
    ...(showOrganisation
      ? [col.text<MonitoringEvent>("org", t("admin.insights.columns.organisation"), (e) => e.organisation_name ?? e.organisation_id,
                                   { width: "14%" })]
      : []),
    col.text<MonitoringEvent>("reference", t("admin.insights.columns.reference"),
      (e) => [shortRef(e.external_ref_type), shortRef(e.external_ref_id)].filter(Boolean).join(" / "), { mono: true }),
    col.money<MonitoringEvent>("cost", t("admin.insights.columns.cost"), (e) => ({
      amount: e.cost_amount, currency: e.currency,
      note: e.cost_amount === null ? t("admin.insights.unpriced") : undefined, warn: e.cost_amount === null,
    })),
  ];
  return (
    <AdminTable
      columns={columns}
      data={items}
      getRowKey={(e) => e.id}
      loading={loading}
      emptyMessage={t("admin.insights.empty.events")}
      infiniteScroll={infiniteScroll}
      maxHeight={maxHeight}
    />
  );
};

export default EventsTable;
